import * as React from "react";
import {
  Box,
  CardMedia,
  Divider,
  Stack,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useContext } from "react";
import Modal from "../Modal/Modal";
import { de } from "date-fns/locale";
import Timeline from "@mui/lab/Timeline";
import { format } from "date-fns/format";
import PageTitle from "../Displays/PageTitle";
import TimelineDot from "@mui/lab/TimelineDot";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { Tariff } from "src/models/clientsModel";
import TimelineItem from "@mui/lab/TimelineItem";
import { ThemeValues } from "../ThemeUsage/theme";
import { dateFormat } from "src/helpers/dateFormat";
import TimelineContent from "@mui/lab/TimelineContent";
import { ThemeContext } from "../ThemeUsage/ThemeUsage";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import { timelineItemClasses } from "@mui/lab/TimelineItem";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import ModalContent from "src/pages/Tarif/components/ModalContent";
import { BONUS, SESSION_KEYS, SITE_TECHNICAL_DATA } from "src/consts/session";
import DetailsModalContent from "src/pages/Tarif/components/DetailsModalContent";
import {
  ChevronDown,
  IsonHouse,
  QuestionMark,
  CheckCircle,
  SmaHouse,
} from "src/assets";
import NewLichtblickLogo from "src/assets/icons/lichtblick-tariff-selection.svg";
import { useState } from "react";
import CheckWhite from "src/assets/icons/checkWhite.svg";

interface TariffTimelineProps {
  tariffsData?: Tariff[];
  showSavingsResult: boolean;
}

const TariffTimeline = ({
  tariffsData,
  showSavingsResult,
}: TariffTimelineProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const themeName = useContext(ThemeContext);
  const currentMonth = format(new Date(), "LLLL", { locale: de });

  const [isExpanded, setIsExpanded] = useState(false);

  const [getSiteTechnicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const campaignBonusDisplayName = getBonus()?.logicalId ?? "";
  const campaignBonusValidTill = getBonus()?.endDate ?? "";

  const showCampaignText = campaignBonusDisplayName.length > 0;
  const showBonusValidTillText = campaignBonusValidTill.length > 0;
  const bonusValidTillDate =
    showBonusValidTillText && dateFormat(campaignBonusValidTill);

  const isSmartMeterInstalled =
    (getSiteTechnicalData()?.smartMeterInstalled ?? "") === "true";

  const [isOverlayOpenFix, toggleIsOverlayOpenFix] = useToggle(false);
  const [isOverlayOpenFlex, toggleIsOverlayOpenFlex] = useToggle(false);

  const savingsData = tariffsData && tariffsData[0];
  const fixTariffDisplayName =
    (savingsData && savingsData.fixTariff?.tariffDisplayName) ?? "";
  const fixBasePriceMonthly =
    (savingsData && savingsData.fixTariff?.basePriceMonthly) ?? "";
  const fixWorkingPricePerKwh =
    (savingsData && savingsData.fixTariff?.workingPricePerKwh) ?? "";
  const fixMonthlyCost =
    (savingsData && savingsData.fixTariff?.monthlyCosts) ?? "";

  const costSmartMeterMonthly =
    savingsData && savingsData.savings?.costSmartMeterMonthly;

  const flexTariffDisplayName =
    (savingsData && savingsData.flexTariff?.tariffDisplayName) ?? "";

  const savingsYearly = savingsData && savingsData.savings?.savingsYearly;

  const flexBasePriceMonthly =
    (savingsData && savingsData.flexTariff?.basePriceMonthly) ?? "";
  const flexWorkingPricePerKwh =
    (savingsData && savingsData.flexTariff?.workingPricePerKwh) ?? "";
  const flexSupplyPriceFixedElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFixedElementPerKwh) ??
    "";
  const flexSupplyPriceFlexElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFlexElementPerKwh) ?? "";

  const estimatedYearlyCostsRaw = savingsData?.flexTariff?.estimatedYearlyCosts;

  const estimatedYearlyCostsNumber: number | null =
    estimatedYearlyCostsRaw != null
      ? Number(String(estimatedYearlyCostsRaw).replace(",", "."))
      : null;

  const estimatedMonthlyCosts: string =
    estimatedYearlyCostsNumber !== null
      ? (estimatedYearlyCostsNumber / 12).toFixed(2).replace(".", ",")
      : "-- ; --";

  const getLogo = () => {
    let houseImg = "";
    let cssProps = {};
    switch (themeName) {
      case ThemeValues.SMA:
        houseImg = SmaHouse;
        cssProps = {
          mt: "-30px",
          width: "120px",
          height: "87px",
          objectFit: "contain",
          borderRadius: 3,
        };
        break;
      default:
        houseImg = IsonHouse;
        cssProps = {
          mt: "-20px",
          width: "151px",
          height: "87px",
          objectFit: "contain",
        };
        break;
    }
    return {
      houseImg,
      cssProps,
    };
  };

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
          margin: 0,
          padding: 0,
        }}
      >
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot
              sx={(theme) => ({
                padding: 0,
                width: "8px",
                height: "8px",
                background: theme.palette.app.primaryFont,
              })}
            ></TimelineDot>
            <TimelineConnector
              sx={(theme) => ({
                border: "1px dashed #ffffffff",
              })}
            />
          </TimelineSeparator>

          <TimelineContent
            sx={{
              paddingRight: 0,
              mt: -4,
              paddingBottom: 8,
            }}
          >
            <Stack gap={2} sx={{}}>
              <Accordion
                elevation={0}
                square
                disableGutters
                sx={(theme) => ({
                  padding: 0,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  transition: "background-color 0.2s ease",
                  color: "black",
                })}
              >
                <AccordionSummary
                  expandIcon={<ChevronDown />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{ padding: 0, px: 3 }}
                >
                  <Stack gap={0}>
                    <Text
                      sx={{
                        fontSize: "11px",
                        fontWeight: "700",
                        lineHeight: "15px",
                        color: "inherit",
                        fontFamily: "inherit",
                      }}
                      text={`${t(
                        "page.tariff.timeline.timelineItem1.seperatorText",
                      )} ${fixTariffDisplayName}`}
                    />
                    <Stack gap={0.5} direction="row" alignItems="center">
                      <Text
                        sx={{
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                          mt: -1,
                          color: "inherit",
                          fontFamily: "inherit",
                        }}
                        text={`${t(
                          "page.tariff.timeline.timelineItem1.subtitle",
                        )}: ${fixMonthlyCost}€/mtl.`}
                      />
                      <Text
                        sx={{
                          fontSize: "8px",
                          marginTop: -2,
                          marginLeft: -0.5,
                          fontFamily: "isonType",
                          color: "inherit",
                        }}
                        text="2"
                      />
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: 0 }}>
                  <Stack
                    sx={{
                      px: 3,
                      py: 3,
                      gap: 4,
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <Text
                      sx={{
                        fontSize: "11px",
                        fontWeight: "400",
                        lineHeight: "15px",
                        fontFamily: "Inter",
                        color: "inherit",
                      }}
                      text={
                        <>
                          {t("page.tariff.timeline.timelineItem1.description")}
                          <QuestionMark
                            onClick={toggleIsOverlayOpenFix}
                            style={{
                              verticalAlign: "middle",
                              marginLeft: "8px",
                              cursor: "pointer",
                            }}
                          />
                        </>
                      }
                    />

                    <Stack gap={1}>
                      <Text
                        sx={{
                          fontSize: "11px",
                          fontWeight: "700",
                          lineHeight: "15px",
                          color: "inherit",
                        }}
                        text={t(
                          "page.tariff.timeline.timelineItem1.list.title",
                        )}
                      />

                      <Stack direction="row" justifyContent="space-between">
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                            color: "inherit",
                          }}
                          text={`${t(
                            "page.tariff.timeline.timelineItem1.list.listItem1",
                          )}:`}
                        />
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                            color: "inherit",
                          }}
                          text={t(
                            "page.tariff.timeline.timelineItem1.list.listItem1Value",
                            { fixBasePriceMonthly },
                          )}
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                            color: "inherit",
                          }}
                          text={`${t(
                            "page.tariff.timeline.timelineItem1.list.listItem2",
                          )} (${currentMonth}):`}
                        />
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                            color: "inherit",
                          }}
                          text={`${fixWorkingPricePerKwh} ct/kWh`}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </TimelineContent>
        </TimelineItem>

        {!isSmartMeterInstalled && (
          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot
                sx={{
                  padding: 0,
                  width: "8px",
                  height: "8px",
                  background: theme.palette.app.primaryFont,
                }}
              />
              <TimelineConnector
                sx={{
                  background: theme.palette.app.primaryFont,
                }}
              />
            </TimelineSeparator>

            <TimelineContent
              sx={{
                paddingRight: 0,
                mt: -4,
                paddingBottom: 8,
              }}
            >
              <Stack gap={2} sx={{}}>
                <Accordion
                  elevation={0}
                  square
                  disableGutters
                  sx={(theme) => ({
                    padding: 0,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    transition: "background-color 0.2s ease",
                    color: "black",
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                    sx={(theme) => ({
                      padding: 0,
                      px: 3,
                    })}
                  >
                    <Stack gap={2}>
                      <Text
                        sx={(theme) => ({
                          fontSize: { xs: "11px", sm: "11px" },
                          fontWeight: "700",
                          lineHeight: { xs: "11px", sm: "22px" },
                        })}
                        text={t(
                          "page.tariff.timeline.timelineItem2.seperatorText",
                        )}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: 0,
                      mb: 4,
                    }}
                  >
                    <Stack
                      sx={{
                        px: 3,
                        py: 3,
                        gap: 4,
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                      }}
                    >
                      <Stack gap={2}>
                        <Stack direction="row" gap={2}>
                          <Box>
                            <CheckCircle />
                          </Box>
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            }}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description1",
                            )}
                          />
                        </Stack>

                        <Stack direction="row" gap={2}>
                          <Box>
                            <CheckCircle />
                          </Box>
                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description2",
                            )}
                          />
                        </Stack>

                        <Stack direction="row" gap={2}>
                          <Box>
                            <CheckCircle />
                          </Box>
                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description3",
                            )}
                          />
                        </Stack>
                      </Stack>

                      <Stack gap={1}>
                        <Text
                          sx={(theme) => ({
                            fontSize: "11px",
                            fontWeight: "700",
                            lineHeight: "15px",
                          })}
                          text={t(
                            "page.tariff.timeline.timelineItem2.list.title",
                          )}
                        />

                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                            })}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem1",
                            )}:`}
                          />

                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={`0,00 €`}
                          />
                        </Stack>

                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                            })}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem2",
                            )}:`}
                          />

                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={`${costSmartMeterMonthly} ${t(
                              "page.tariff.timeline.timelineItem2.list.listItem2Value",
                            )}`}
                          />
                        </Stack>

                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                            })}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem3",
                            )}:`}
                          />

                          <Text
                            sx={(theme) => ({
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={t(
                              "page.tariff.timeline.timelineItem2.list.listItem3Value",
                            )}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </TimelineContent>
          </TimelineItem>
        )}

        <Stack gap={2} sx={{ mt: -2 }}>
          <Stack sx={{ bgcolor: "#DAECFA", borderRadius: "12px" }}>
            <Accordion
              expanded={isExpanded}
              disableGutters
              onChange={(event: React.SyntheticEvent, expanded: boolean) =>
                setIsExpanded(expanded)
              }
              elevation={0}
              square={true}
              sx={{
                padding: 0,
                borderRadius: "8px",
                backgroundColor: "#084465",
              }}
            >
              <AccordionSummary
                aria-controls="panel3-content"
                id="panel3-header"
                sx={{ padding: 0, px: 1 }}
              >
                <Stack gap={1} width="100%">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      backgroundColor: "#105A83",
                      px: 3,
                      py: 3,
                      borderRadius: 2,
                      width: "98%",
                      margin: "0 auto",
                      marginTop: "-3px",
                    }}
                  >
                    <Stack
                      direction="column"
                      alignItems="flex-start"
                      gap={{ xs: 1, sm: 2 }}
                    >
                      <Text
                        sx={{
                          color: "#FFFFFF",
                          fontSize: { xs: "14px", sm: "14px" },
                          fontWeight: 700,
                          lineHeight: "17px",
                        }}
                        text={flexTariffDisplayName}
                      />
                      <Box
                        component="img"
                        src={NewLichtblickLogo}
                        alt="Ökostrom Icon"
                        sx={{
                          height: { xs: 16, sm: 20 },
                          width: "auto",
                        }}
                      />
                    </Stack>

                    <Stack
                      direction="column"
                      alignItems="flex-end"
                      sx={{ color: "#FFFFFF" }}
                    >
                      <Stack direction="row">
                        <Stack gap={0} direction="row" alignItems="center">
                          <Text
                            sx={{
                              color: "#FFFFFF",
                              fontSize: "15px",
                              fontWeight: 700,
                            }}
                            text={<>{`${estimatedMonthlyCosts} €/mtl.`}</>}
                          />
                          <Text
                            sx={{
                              color: "#FFFFFF",
                              marginTop: -1,
                              fontSize: { xs: "10px", sm: "10px" },
                              fontFamily: "isonType",
                              fontWeight: 700,
                            }}
                            text={`1`}
                          />
                        </Stack>
                      </Stack>

                      <Text
                        sx={{ color: "#FFFFFF", fontSize: "10px", mt: -2 }}
                        text={t(
                          "page.tariff.tariffSelectionContainer.calculatedCosts",
                        )}
                      />
                    </Stack>
                  </Stack>

                  <Stack
                    direction={{ xs: "row", sm: "row" }}
                    spacing={1.5}
                    mt={2}
                    sx={{ padding: 0, px: { xs: 6, sm: 18 } }}
                    width="100%"
                    justifyContent="space-between"
                  >
                    {[
                      t("page.calculatedSavingsSummary.checkbox.point1"),
                      t("page.calculatedSavingsSummary.checkbox.point3"),
                    ].map((feature, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 2 }}
                      >
                        <Box
                          component="img"
                          src={CheckWhite}
                          sx={{
                            width: { xs: 10, sm: 10 },
                            height: { xs: 10, sm: 10 },
                          }}
                        />
                        <Text
                          text={feature}
                          sx={{
                            fontSize: { xs: "10px", sm: "12px" },
                            color: "#FFFFFF",
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: 0,
                }}
              >
                <Stack
                  sx={{
                    px: 3,
                    py: 3,
                    gap: 4,
                    bgcolor: "#DAECFA",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                  }}
                >
                  <Text
                    sx={{
                      margin: 0,
                      color: "#222",
                      fontSize: "11px",
                      fontWeight: "400",
                      lineHeight: "15px",
                      fontFamily: "Inter",
                    }}
                    text={
                      <>
                        {t("page.tariff.timeline.timelineItem3.description")}
                        <QuestionMark
                          onClick={toggleIsOverlayOpenFlex}
                          style={{
                            verticalAlign: "middle",
                            marginLeft: "8px",
                            cursor: "pointer",
                          }}
                        />
                      </>
                    }
                  />

                  <Stack gap={2}>
                    <Stack gap={1}>
                      <Text
                        sx={(theme) => ({
                          color: "#222",
                          fontSize: "11px",
                          fontWeight: "700",
                          lineHeight: "15px",
                        })}
                        text={t(
                          "page.tariff.timeline.timelineItem3.list.title",
                        )}
                      />

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "600",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem1",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${flexBasePriceMonthly} ${t(
                            "page.tariff.timeline.timelineItem3.list.listItem1Value",
                          )}`}
                        />
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "600",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem2",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${flexWorkingPricePerKwh} ct/kWh`}
                        />
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                            padding: "0 0 0 15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem3",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${flexSupplyPriceFixedElementPerKwh} ct/kWh`}
                        />
                      </Stack>

                      <Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Text
                            sx={(theme) => ({
                              color: "#222",
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                              padding: "0 0 0 15px",
                            })}
                            text={`${t(
                              "page.tariff.timeline.timelineItem3.list.listItem4",
                            )}:`}
                          />

                          <Text
                            sx={(theme) => ({
                              color: "#222",
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                            })}
                            text={`${flexSupplyPriceFlexElementPerKwh} ct/kWh`}
                          />
                        </Stack>

                        <Stack direction={"row"} gap={1}>
                          <Text
                            sx={(theme) => ({
                              color: "#848F9F",
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                              padding: "0 0 0 15px",
                            })}
                            text={`${t(
                              "page.tariff.timeline.timelineItem3.list.listItem12",
                            )}`}
                          />
                        </Stack>
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem5",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem5Value",
                          )}`}
                        />
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem6",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem6Value",
                          )}`}
                        />
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem10",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem10Value",
                          )}`}
                        />
                      </Stack>

                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem11",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem11Value",
                          )}`}
                        />
                      </Stack>
                    </Stack>

                    <Divider />

                    <Stack gap={1}>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "600",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem7",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem7Value",
                          )}`}
                        />
                      </Stack>
                      <Text
                        sx={(theme) => ({
                          color: "#222",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        })}
                        text={`${t(
                          "page.tariff.timeline.timelineItem3.list.listItem8",
                        )}`}
                      />
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem9",
                          )}:`}
                        />

                        <Text
                          sx={(theme) => ({
                            color: "#222",
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                          })}
                          text={`${t(
                            "page.tariff.timeline.timelineItem3.list.listItem9Value",
                          )}`}
                        />
                      </Stack>
                    </Stack>

                    <Divider />

                    <Stack gap={1}>
                      <Text
                        sx={{
                          margin: 0,
                          color: "#222",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                          fontFamily: "Inter",
                        }}
                        text={t(
                          "page.tariff.timeline.timelineItem3.electricityPriceCalculation1",
                        )}
                      />
                      <Text
                        sx={{
                          margin: 0,
                          color: "#222",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                          fontFamily: "Inter",
                        }}
                        text={t(
                          "page.tariff.timeline.timelineItem3.electricityPriceCalculation2",
                        )}
                      />
                    </Stack>
                  </Stack>
                  <Stack
                    gap={2}
                    direction="row"
                    alignItems={"center"}
                    justifyContent={"center"}
                    sx={{
                      cursor: "pointer",
                      width: "100%",
                    }}
                    onClick={() => {
                      setIsExpanded(false);
                    }}
                  >
                    <Text
                      sx={(theme) => ({
                        color: "#000",
                        fontSize: "11px",
                        fontWeight: "400",
                        lineHeight: "15px",
                        textDecoration: "underline",
                      })}
                      text={t(
                        "page.tariff.tariffSelectionContainer.closeTariffDetails",
                      )}
                    />
                    <ChevronDown style={{ transform: "rotate(180deg)" }} />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {!isExpanded && (
              <Stack
                gap={1}
                direction="row"
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  mt: themeName === ThemeValues.SMA ? 5 : 4,
                  cursor: "pointer",
                  width: "100%",
                }}
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                <Text
                  sx={(theme) => ({
                    color: "#000",
                    fontSize: "11px",
                    fontWeight: "400",
                    lineHeight: "15px",
                    textDecoration: "underline",
                  })}
                  text={t(
                    "page.tariff.tariffSelectionContainer.viewTariffDetails",
                  )}
                />
                <ChevronDown />
              </Stack>
            )}
            {showSavingsResult && (
              <Stack
                direction={"column"}
                sx={(theme) => ({
                  mt: themeName === ThemeValues.SMA ? 6 : 3,
                  borderRadius: 3,
                  bgcolor: "#C6FFB2",
                  border: "1px solid #009A22",
                })}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  sx={(theme) => ({
                    justifyContent: "space-between",
                  })}
                >
                  <Stack pl={3}>
                    <Text
                      sx={(theme) => ({
                        color: "#222",
                        fontSize: {
                          xs: "9px",
                          sm: "11px",
                        },
                        fontWeight: "400",
                        lineHeight: "15px",
                      })}
                      text={`${t(
                        "page.calculatedSavingsSummary.approxSavings",
                      )}`}
                    />

                    <Stack gap={0.5} direction={"row"} alignItems={"center"}>
                      <Text
                        sx={{
                          color: "#009A22",
                          fontSize: "20px",
                          fontWeight: "700",
                          lineHeight: "25px",
                          fontFamily: "isonType",
                        }}
                        text={`${savingsYearly} €`}
                      />

                      <Text
                        sx={{
                          marginTop: -3,
                          color: "#009A22",
                          fontSize: { xs: "8px", sm: "10px" },
                          fontWeight: "700",
                          lineHeight: "25px",
                          fontFamily: "isonType",
                        }}
                        text={`1`}
                      />
                    </Stack>
                  </Stack>

                  <CardMedia
                    component="img"
                    alt="houseLogo"
                    sx={getLogo().cssProps}
                    image={getLogo().houseImg}
                  />
                </Stack>

                {showCampaignText && (
                  <Stack sx={{ width: 1 }}>
                    <Stack
                      alignItems={"center"}
                      sx={(theme) => ({
                        mt: themeName === ThemeValues.SMA ? 10 : 8,
                        borderRadius: 3,
                        bgcolor: "#48BD57",
                        border: "1px solid #48BD57",
                        margin: 1,
                        padding: 1,
                      })}
                    >
                      {showBonusValidTillText && (
                        <Text
                          text={`${t(
                            `page.tariff.timeline.onlyTill`,
                          )} ${bonusValidTillDate}`}
                          sx={{
                            mb: -2,
                            color: "#E9FFE1",
                            fontSize: "15px",
                            fontWeight: 400,
                            lineHeight: "25px",
                          }}
                        />
                      )}

                      <Text
                        text={
                          <>
                            {t(
                              `page.tariff.timeline.bonusSavings.${campaignBonusDisplayName}` as any,
                            )}
                            <sup>3</sup>
                          </>
                        }
                        sx={{
                          color: "#E9FFE1",
                          fontSize: "20px",
                          fontWeight: 700,
                          lineHeight: "25px",
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Timeline>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.electricity.modalTitle1", {
              fixTariffDisplayName,
            })}
          />
        }
        isOpen={isOverlayOpenFix}
        onClose={toggleIsOverlayOpenFix}
        onSubmit={toggleIsOverlayOpenFix}
      >
        <DetailsModalContent />
      </Modal>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.electricity.modalTitle2", {
              flexTariffDisplayName,
            })}
          />
        }
        isOpen={isOverlayOpenFlex}
        onClose={toggleIsOverlayOpenFlex}
        onSubmit={toggleIsOverlayOpenFlex}
      >
        <ModalContent />
      </Modal>
    </>
  );
};

export default TariffTimeline;
